import { Field, useFormikContext } from 'formik';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NativeSyntheticEvent,
  Platform,
  TextInputKeyPressEventData,
} from 'react-native';

import { Box } from '@components/Restyle';
import TextField from '@components/shared/TextField/TextField';
import { TASK_DESC_LENGTH_LIMIT } from '@src/constants/constants';
import theme from '@themes/theme';

interface EditTaskDescriptionProps {
  disabled: boolean;
  maxLength?: number;
  placeholder?: string;
  hideBorder?: boolean;
  blurOnEnter?: boolean;
  hideScrollbar?: boolean;
  showCharsRemaining?: boolean;
  onChange?: (value: string) => void;
  autoFocus?: boolean;
}

export const EditTaskDescription: React.FC<EditTaskDescriptionProps> = ({
  disabled,
  maxLength = TASK_DESC_LENGTH_LIMIT,
  placeholder,
  hideBorder = false,
  blurOnEnter = false,
  hideScrollbar = false,
  showCharsRemaining = true,
  onChange,
  autoFocus = true,
}) => {
  const { t } = useTranslation();
  const { submitForm } = useFormikContext();
  const defaultPlaceholder = t(
    'models:tasks.placeholders.descriptionShortModal'
  );

  useEffect(() => {
    if (
      Platform.OS !== 'web' ||
      !hideScrollbar ||
      typeof document === 'undefined'
    )
      return;
    const style = document.createElement('style');
    style.textContent = [
      '#project-desc-field textarea { scrollbar-width: none; }',
      '#project-desc-field textarea::-webkit-scrollbar { display: none; }',
    ].join('\n');
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, [hideScrollbar]);

  const handleKeyPress = useCallback(
    (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      if (Platform.OS !== 'web') return;
      const nativeEvent = e.nativeEvent as unknown as KeyboardEvent;
      if (nativeEvent.key === 'Enter' && !nativeEvent.shiftKey) {
        e.preventDefault();
        if (blurOnEnter) {
          (nativeEvent.target as HTMLElement)?.blur();
        } else {
          submitForm();
        }
      }
    },
    [submitForm, blurOnEnter]
  );

  return (
    <Box
      nativeID={hideScrollbar ? 'project-desc-field' : undefined}
      flex={1}
      mt='s'
      borderRadius='xs'
      borderColor={hideBorder ? undefined : 'black'}
      borderWidth={hideBorder ? 0 : 1}
      px={hideBorder ? undefined : 'm'}
      pt={hideBorder ? undefined : 'xxs'}
      pb={hideBorder ? 'm' : 's'}>
      <Field
        component={TextField}
        isRemainingTextAtBottom={true}
        remainingTextVar='webBodyHintSubline'
        label='Description'
        name='description'
        accessible
        placeholder={placeholder || defaultPlaceholder}
        borderless={!hideBorder}
        marginBottom={hideBorder ? 'm' : 'none'}
        hideError
        showCharsRemaining={showCharsRemaining}
        autoFocus={autoFocus}
        multiline
        autoResizeHeight={hideBorder}
        onChangeText={onChange}
        onKeyPress={handleKeyPress}
        textInputProps={{
          height: hideBorder ? undefined : 64,
          maxLength: maxLength,
          paddingTop: 0,
          marginTop: 0,
          marginBottom: 0,
          marginRight: 0,
          readOnly: disabled,
          multiline: true,
          numberOfLines: 4,
          textAlignVertical: 'top',
          style: {
            ...(hideBorder ? { paddingBottom: theme.spacing.m } : {}),
          },
        }}
        accessibilityLabel='Description'
      />
    </Box>
  );
};
